/**
 * REPORTS SERVICE
 * Central API service for all report endpoints
 * Follows existing ApiService + API_HOST + ENDPOINTS pattern
 */

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_HOST } from '../../../../core/api/api.config';
import { ApiService } from '../../../../core/api/api.service';
import { ENDPOINTS } from '../../../../core/api/endpoints';
import { DisbursementReportRawData, MprDocument, QprDocument, ReportDateRange } from '../models/report.model';

@Injectable({ providedIn: 'root' })
export class ReportService {
  constructor(private api: ApiService) {}

  /**
   * Get Disbursement Report
   * Shows what was disbursed in each quarter
   */
  getDisbursementReport(dateRange: ReportDateRange): Observable<DisbursementReportRawData[]> {
    const startDate = this.formatDateForAPI(dateRange.startDate);
    const endDate = this.formatDateForAPI(dateRange.endDate);

    return this.api.get(
      API_HOST.reports,
      `${ENDPOINTS.REPORTS.DISBURSEMENT_REPORT}${startDate}/${endDate}`,
    );
  }

  /**
   * Get Repayment Report
   * Shows what was paid back in each quarter
   */
  getRepaymentReport(dateRange: ReportDateRange): Observable<DisbursementReportRawData[]> {
    const startDate = this.formatDateForAPI(dateRange.startDate);
    const endDate = this.formatDateForAPI(dateRange.endDate);

    return this.api.get(
      API_HOST.reports,
      `${ENDPOINTS.REPORTS.REPAYMENT_REPORT}${startDate}/${endDate}`,
    );
  }

  /**
   * Get Exposure Report
   * Shows current outstanding exposure
   */
getExposureReport(
  dateRange: ReportDateRange,
): Observable<DisbursementReportRawData[]> {
  const startDate = this.formatDateForAPI(dateRange.startDate);
  const endDate = this.formatDateForAPI(dateRange.endDate);

  return this.api.get(
    API_HOST.reports,
    `${ENDPOINTS.REPORTS.EXPOSURE_REPORT}${startDate}/${endDate}`,
  );
}

  /**
   * Get Approved Facilities Report
   * Backend returns the report file directly (binary); downloaded as-is.
   */
  getApprovedFacilitiesReport(dateRange: ReportDateRange): Observable<Blob> {
    const startDate = this.formatDateForAPI(dateRange.startDate);
    const endDate = this.formatDateForAPI(dateRange.endDate);

    return this.api.get(
      API_HOST.reports,
      `${ENDPOINTS.REPORTS.APPROVED_FACILITIES_REPORT}${startDate}/${endDate}`,
      { responseType: 'blob' },
    );
  }

  /**
   * Get Projection Report
   * Backend returns the report file directly (binary); downloaded as-is.
   */
  getProjectionReport(dateRange: ReportDateRange): Observable<Blob> {
    const startDate = this.formatDateForAPI(dateRange.startDate);
    const endDate = this.formatDateForAPI(dateRange.endDate);

    return this.api.get(
      API_HOST.reports,
      `${ENDPOINTS.REPORTS.PROJECTION_REPORT}${startDate}/${endDate}`,
      { responseType: 'blob' },
    );
  }

  /**
   * Get Enhanced Approved Facilities Report
   * Backend returns the report file directly (binary); downloaded as-is.
   */
  getEnhancedApprovedFacilitiesReport(dateRange: ReportDateRange): Observable<Blob> {
    const startDate = this.formatDateForAPI(dateRange.startDate);
    const endDate = this.formatDateForAPI(dateRange.endDate);

    return this.api.get(
      API_HOST.reports,
      `${ENDPOINTS.REPORTS.ENHANCED_APPROVED_FACILITIES_REPORT}${startDate}/${endDate}`,
      { responseType: 'blob' },
    );
  }

  /**
   * Get Consolidated QPR Report
   * Backend returns the report file directly (binary); downloaded as-is.
   */
  getConsolidatedQprReport(quarter: string, year: number): Observable<Blob> {
    return this.api.get(
      API_HOST.qpr_reports,
      `${ENDPOINTS.REPORTS.CONSOLIDATED_QPR_REPORT}?qrtr=${encodeURIComponent(quarter)}&year=${year}`,
      { responseType: 'blob' },
    );
  }

  /**
   * Get PB Consolidated QPR Report
   * GET /qpr-reports/get-consolidated-qpr-rprt?qrtr=&year= — file downloaded as-is.
   */
  getPbConsolidatedQprReport(quarter: string, year: number): Observable<Blob> {
    return this.api.get(
      API_HOST.qpr_reports,
      `${ENDPOINTS.REPORTS.PB_CONSOLIDATED_QPR_REPORT}?qrtr=${encodeURIComponent(quarter)}&year=${year}`,
      { responseType: 'blob' },
    );
  }

  /**
   * Get Consolidated MPR Report for a date range.
   * Backend returns the report file directly (binary); downloaded as-is.
   */
  getConsolidatedMprReport(dateRange: ReportDateRange): Observable<Blob> {
    const startDate = this.formatDateForAPI(dateRange.startDate);
    const endDate = this.formatDateForAPI(dateRange.endDate);

    return this.api.get(
      API_HOST.reports,
      `${ENDPOINTS.REPORTS.CONSOLIDATED_MPR_REPORT}?startDate=${startDate}&endDate=${endDate}`,
      { responseType: 'blob' },
    );
  }

  /**
   * Upload the QPR data file.
   * `clntSeq`, `qrtr` and the file all travel in the multipart/form-data body.
   */
  uploadQprReport(clntSeq: number, qrtr: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('clntSeq', String(clntSeq));
    formData.append('qrtr', qrtr);
    formData.append('file', file);

    return this.api.post(API_HOST.reports, ENDPOINTS.REPORTS.QPR_REPORT, formData);
  }

  /**
   * Confirm (aprvdFlg = 1) or cancel (aprvdFlg = 0) the QPR report that was
   * just validated by `uploadQprReport`.
   */
  submitQprReport(payload: {
    clntSeq: number;
    qrtr: string;
    aprvdFlg: number;
  }): Observable<any> {
    return this.api.post(API_HOST.reports, ENDPOINTS.REPORTS.QPR_SUBMIT, payload);
  }

  /**
   * Get all QPR documents. Fetched once and filtered client-side by clntSeq.
   */
  getQprDocuments(): Observable<QprDocument[]> {
    return this.api.get<QprDocument[]>(API_HOST.reports, ENDPOINTS.REPORTS.QPR_DOCUMENTS);
  }

  /**
   * Upload the MPR data file.
   * `clntSeq`, `mnth` and the file all travel in the multipart/form-data body.
   */
  uploadMprReport(clntSeq: number, mnth: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('clntSeq', String(clntSeq));
    formData.append('mnth', mnth);
    formData.append('file', file);

    return this.api.post(API_HOST.reports, ENDPOINTS.REPORTS.MPR_REPORT, formData);
  }

  /**
   * Confirm (aprvdFlg = 1) or cancel (aprvdFlg = 0) the MPR report that was
   * just validated by `uploadMprReport`.
   */
  submitMprReport(payload: {
    clntSeq: number;
    mnth: string;
    aprvdFlg: number;
  }): Observable<any> {
    return this.api.post(API_HOST.reports, ENDPOINTS.REPORTS.MPR_SUBMIT, payload);
  }

  /**
   * Get all MPR documents. Fetched once and filtered client-side by clntSeq.
   */
  getMprDocuments(): Observable<MprDocument[]> {
    return this.api.get<MprDocument[]>(API_HOST.reports, ENDPOINTS.REPORTS.MPR_DOCUMENTS);
  }

  /**
   * Helper: Format date to API format (YYYY-MM-DD)
   */
  private formatDateForAPI(dateInput: Date | string): string {
    let date: Date;

    if (typeof dateInput === 'string') {
      date = new Date(dateInput);
    } else {
      date = dateInput;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
